import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalAdmin, setOriginalAdmin] = useState(null);
  
  // 2FA state
  const [twoFactorPending, setTwoFactorPending] = useState(null);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem("token");
      const adminToken = localStorage.getItem("adminToken");
      const adminUser = localStorage.getItem("adminUser");
      
      // Check if we're in impersonation mode
      if (adminToken && adminUser) {
        setIsImpersonating(true);
        setOriginalAdmin(JSON.parse(adminUser));
      }
      
      if (storedToken) {
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` },
          });
          setUser(response.data);
          setToken(storedToken);
        } catch (error) {
          localStorage.removeItem("token");
          localStorage.removeItem("adminToken");
          localStorage.removeItem("adminUser");
          setToken(null);
          setUser(null);
          setIsImpersonating(false);
          setOriginalAdmin(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password, totpCode = null) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password,
      totp_code: totpCode,
    });
    
    const data = response.data;
    
    // Check if 2FA is required
    if (data.requires_2fa) {
      setTwoFactorPending({
        temp_token: data.temp_token,
        totp_setup_required: data.totp_setup_required,
        qr_code: data.qr_code,
        secret: data.secret,
        email: email,
      });
      return { requires_2fa: true, setup_required: data.totp_setup_required };
    }
    
    // Full login successful
    const { access_token, user: userData, must_reset_password } = data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser({ ...userData, must_reset_password });
    setTwoFactorPending(null);
    return { ...userData, must_reset_password };
  };

  const verify2FA = async (totpCode) => {
    if (!twoFactorPending) {
      throw new Error("No 2FA verification pending");
    }
    
    const endpoint = twoFactorPending.totp_setup_required 
      ? `${API_URL}/api/auth/2fa/setup`
      : `${API_URL}/api/auth/2fa/verify`;
    
    const response = await axios.post(endpoint, {
      temp_token: twoFactorPending.temp_token,
      totp_code: totpCode,
    });
    
    const { access_token, user: userData, must_reset_password } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser({ ...userData, must_reset_password });
    setTwoFactorPending(null);
    return { ...userData, must_reset_password };
  };

  const cancel2FA = () => {
    setTwoFactorPending(null);
  };

  const register = async (name, email, password, role = "employee") => {
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      name,
      email,
      password,
      role,
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    setToken(null);
    setUser(null);
    setIsImpersonating(false);
    setOriginalAdmin(null);
    setTwoFactorPending(null);
  };

  const impersonate = async (userId) => {
    // Store current admin credentials before impersonating
    const currentToken = localStorage.getItem("token");
    localStorage.setItem("adminToken", currentToken);
    localStorage.setItem("adminUser", JSON.stringify(user));
    
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/impersonate/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      
      const { access_token, user: impersonatedUser } = response.data;
      localStorage.setItem("token", access_token);
      setToken(access_token);
      setUser(impersonatedUser);
      setIsImpersonating(true);
      setOriginalAdmin(user);
      
      return impersonatedUser;
    } catch (error) {
      // Restore admin token if impersonation fails
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminUser");
      throw error;
    }
  };

  const stopImpersonating = () => {
    const adminToken = localStorage.getItem("adminToken");
    const adminUser = localStorage.getItem("adminUser");
    
    if (adminToken && adminUser) {
      localStorage.setItem("token", adminToken);
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminUser");
      setToken(adminToken);
      setUser(JSON.parse(adminUser));
      setIsImpersonating(false);
      setOriginalAdmin(null);
    }
  };

  const getAuthHeader = () => ({
    Authorization: `Bearer ${token}`,
  });

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        register,
        logout,
        getAuthHeader,
        isAdmin: user?.role === "admin",
        tracksStats: user?.tracks_stats !== false,
        tracksBillings: user?.tracks_billings === true,
        isSupervisor: user?.is_supervisor === true,
        isImpersonating,
        originalAdmin,
        impersonate,
        stopImpersonating,
        // 2FA
        twoFactorPending,
        verify2FA,
        cancel2FA,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
